import React, { useState, useMemo } from 'react';
import { Search, Loader2, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useInfluencerRecipients } from '@/domains/influencers/hooks/useInfluencerRecipients';
import { useContacts } from '@/hooks/useContacts';
import { useBrands } from '@/hooks/useBrands';
import { isValidEmail } from '@/types/recipient.types';
import type {
  RecipientItem,
  InfluencerRecipient,
  ContactRecipient,
  BrandRecipient,
} from '@/types/recipient.types';

interface RecipientSelectorProps {
  selectedRecipients: RecipientItem[];
  onRecipientsChange: (recipients: RecipientItem[]) => void;
}

export const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  selectedRecipients,
  onRecipientsChange,
}) => {
  const [activeTab, setActiveTab] = useState<'influencers' | 'contacts' | 'brands'>('influencers');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const isSearchPending = searchTerm.trim() !== debouncedSearch.trim();

  // Influencer Keyset Infinite Query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
  } = useInfluencerRecipients({
    searchTerm: activeTab === 'influencers' ? debouncedSearch : '',
  });

  // Contacts and Brands Queries
  const { data: contactsData = [], isLoading: loadingContacts } = useContacts();
  const { data: brandsData = [], isLoading: loadingBrands } = useBrands();

  const isBusy = isLoading || isFetching || isSearchPending;

  const influencers: InfluencerRecipient[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data?.pages],
  );

  const deliverableVisibleInfluencers = useMemo(
    () => influencers.filter((i) => i.isDeliverable),
    [influencers],
  );

  const contacts: ContactRecipient[] = useMemo(() => {
    return contactsData.map((c) => {
      const email = c.email?.trim() || '';
      return {
        id: c.id,
        name: c.name || 'Unnamed Contact',
        email,
        type: 'contact',
        isDeliverable: isValidEmail(email),
        companyName: c.company_name || undefined,
        designation: c.designation || undefined,
      };
    });
  }, [contactsData]);

  const filteredContacts = useMemo(() => {
    const q = debouncedSearch.toLowerCase().trim();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.companyName?.toLowerCase().includes(q) ||
        c.designation?.toLowerCase().includes(q),
    );
  }, [contacts, debouncedSearch]);

  const deliverableContacts = useMemo(
    () => filteredContacts.filter((c) => c.isDeliverable),
    [filteredContacts],
  );

  const brands: BrandRecipient[] = useMemo(() => {
    return brandsData.map((b) => {
      const brandRaw = b as unknown as { contactEmail?: string; email?: string };
      const email = brandRaw.contactEmail?.trim() || brandRaw.email?.trim() || '';
      return {
        id: b.id,
        name: b.name || 'Unnamed Brand',
        email,
        type: 'brand',
        isDeliverable: isValidEmail(email),
        industry: b.industry || undefined,
      };
    });
  }, [brandsData]);

  const filteredBrands = useMemo(() => {
    const q = debouncedSearch.toLowerCase().trim();
    if (!q) return brands;
    return brands.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        b.email.toLowerCase().includes(q) ||
        b.industry?.toLowerCase().includes(q),
    );
  }, [brands, debouncedSearch]);

  const deliverableBrands = useMemo(
    () => filteredBrands.filter((b) => b.isDeliverable),
    [filteredBrands],
  );

  const handleSelectVisibleInfluencers = () => {
    if (isBusy) return;
    const existingIds = new Set(
      selectedRecipients.filter((r) => r.type === 'influencer').map((r) => r.id),
    );
    const toAdd = deliverableVisibleInfluencers.filter((i) => !existingIds.has(i.id));
    onRecipientsChange([...selectedRecipients, ...toAdd]);
  };

  const handleToggleInfluencer = (item: InfluencerRecipient) => {
    if (!item.isDeliverable || isBusy) return;
    const exists = selectedRecipients.some((r) => r.id === item.id && r.type === 'influencer');
    if (exists) {
      onRecipientsChange(
        selectedRecipients.filter((r) => !(r.id === item.id && r.type === 'influencer')),
      );
    } else {
      onRecipientsChange([...selectedRecipients, item]);
    }
  };

  const handleToggleContact = (item: ContactRecipient) => {
    if (!item.isDeliverable) return;
    const exists = selectedRecipients.some((r) => r.id === item.id && r.type === 'contact');
    if (exists) {
      onRecipientsChange(
        selectedRecipients.filter((r) => !(r.id === item.id && r.type === 'contact')),
      );
    } else {
      onRecipientsChange([...selectedRecipients, item]);
    }
  };

  const handleToggleBrand = (item: BrandRecipient) => {
    if (!item.isDeliverable) return;
    const exists = selectedRecipients.some((r) => r.id === item.id && r.type === 'brand');
    if (exists) {
      onRecipientsChange(
        selectedRecipients.filter((r) => !(r.id === item.id && r.type === 'brand')),
      );
    } else {
      onRecipientsChange([...selectedRecipients, item]);
    }
  };

  const isSelected = (id: string, type: string) =>
    selectedRecipients.some((r) => r.id === id && r.type === type);

  const selectedInfluencerCount = selectedRecipients.filter((r) => r.type === 'influencer').length;
  const selectedContactCount = selectedRecipients.filter((r) => r.type === 'contact').length;
  const selectedBrandCount = selectedRecipients.filter((r) => r.type === 'brand').length;

  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            <div>
              <CardTitle className="text-lg text-gray-900">Select Recipients</CardTitle>
              <CardDescription>Choose who will receive your personalized email</CardDescription>
            </div>
          </div>
          <Badge variant="secondary" className="px-3 py-1 text-xs">
            {selectedRecipients.length} total selected
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Search Input with Immediate Spinner */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={
              activeTab === 'influencers'
                ? 'Search influencers by name, handle, or email...'
                : activeTab === 'contacts'
                ? 'Search contacts by name, email, or company...'
                : 'Search brands by name or industry...'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-9"
          />
          {activeTab === 'influencers' &&
            (isSearchPending || (isFetching && !isFetchingNextPage)) && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
            )}
        </div>

        {/* Recipient Type Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(val) => {
            setActiveTab(val as 'influencers' | 'contacts' | 'brands');
            setSearchTerm('');
          }}
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="influencers">
              Influencers {selectedInfluencerCount > 0 && `(${selectedInfluencerCount})`}
            </TabsTrigger>
            <TabsTrigger value="contacts">
              Contacts {selectedContactCount > 0 && `(${selectedContactCount})`}
            </TabsTrigger>
            <TabsTrigger value="brands">
              Brands {selectedBrandCount > 0 && `(${selectedBrandCount})`}
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Influencers (Optimized Keyset Pagination) */}
          <TabsContent value="influencers" className="space-y-4 mt-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                {isBusy
                  ? 'Updating results…'
                  : `Loaded: ${influencers.length} | Deliverable: ${deliverableVisibleInfluencers.length}`}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectVisibleInfluencers}
                disabled={isBusy || deliverableVisibleInfluencers.length === 0}
                className="text-xs h-7"
              >
                Select Visible ({deliverableVisibleInfluencers.length})
              </Button>
            </div>

            <div
              className={`border rounded-md divide-y max-h-72 overflow-y-auto transition-opacity ${
                isBusy ? 'opacity-50 pointer-events-none' : 'opacity-100'
              }`}
            >
              {isLoading ? (
                <div className="p-8 text-center text-sm text-gray-500">Loading influencers...</div>
              ) : isError ? (
                <div className="p-8 text-center text-sm text-red-500">
                  Failed to load influencers.
                </div>
              ) : influencers.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">
                  {debouncedSearch
                    ? `No influencers match "${debouncedSearch}"`
                    : 'No influencers found.'}
                </div>
              ) : (
                influencers.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center space-x-3 p-2.5 transition-colors ${
                      item.isDeliverable ? 'hover:bg-gray-50' : 'bg-gray-50/70 opacity-60'
                    }`}
                  >
                    <Checkbox
                      id={`inf-${item.id}`}
                      checked={isSelected(item.id, 'influencer')}
                      onCheckedChange={() => handleToggleInfluencer(item)}
                      disabled={!item.isDeliverable || isBusy}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.name || item.handle}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {item.isDeliverable ? item.email : 'No valid email'}
                        {item.handle && ` • @${item.handle}`}
                      </p>
                    </div>
                    {!item.isDeliverable && (
                      <Badge variant="outline" className="text-[10px] text-gray-400 shrink-0">
                        No email
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </div>

            {hasNextPage && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage || isBusy}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Loading more...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            )}
          </TabsContent>

          {/* Tab 2: Contacts */}
          <TabsContent value="contacts" className="space-y-4 mt-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Total: {filteredContacts.length} | Deliverable: {deliverableContacts.length}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const existingIds = new Set(
                    selectedRecipients.filter((r) => r.type === 'contact').map((r) => r.id),
                  );
                  const toAdd = deliverableContacts.filter((c) => !existingIds.has(c.id));
                  onRecipientsChange([...selectedRecipients, ...toAdd]);
                }}
                disabled={deliverableContacts.length === 0}
                className="text-xs h-7"
              >
                Select Deliverable ({deliverableContacts.length})
              </Button>
            </div>

            <div className="border rounded-md divide-y max-h-72 overflow-y-auto">
              {loadingContacts ? (
                <div className="p-8 text-center text-sm text-gray-500">Loading contacts...</div>
              ) : filteredContacts.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">No contacts found.</div>
              ) : (
                filteredContacts.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center space-x-3 p-2.5 transition-colors ${
                      item.isDeliverable ? 'hover:bg-gray-50' : 'bg-gray-50/70 opacity-60'
                    }`}
                  >
                    <Checkbox
                      id={`contact-${item.id}`}
                      checked={isSelected(item.id, 'contact')}
                      onCheckedChange={() => handleToggleContact(item)}
                      disabled={!item.isDeliverable}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {item.isDeliverable ? item.email : 'No valid email'}
                        {item.companyName && ` • ${item.companyName}`}
                        {item.designation && ` • ${item.designation}`}
                      </p>
                    </div>
                    {!item.isDeliverable && (
                      <Badge variant="outline" className="text-[10px] text-gray-400 shrink-0">
                        No email
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tab 3: Brands */}
          <TabsContent value="brands" className="space-y-4 mt-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Total: {filteredBrands.length} | Deliverable: {deliverableBrands.length}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const existingIds = new Set(
                    selectedRecipients.filter((r) => r.type === 'brand').map((r) => r.id),
                  );
                  const toAdd = deliverableBrands.filter((b) => !existingIds.has(b.id));
                  onRecipientsChange([...selectedRecipients, ...toAdd]);
                }}
                disabled={deliverableBrands.length === 0}
                className="text-xs h-7"
              >
                Select Deliverable ({deliverableBrands.length})
              </Button>
            </div>

            <div className="border rounded-md divide-y max-h-72 overflow-y-auto">
              {loadingBrands ? (
                <div className="p-8 text-center text-sm text-gray-500">Loading brands...</div>
              ) : filteredBrands.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">No brands found.</div>
              ) : (
                filteredBrands.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center space-x-3 p-2.5 transition-colors ${
                      item.isDeliverable ? 'hover:bg-gray-50' : 'bg-gray-50/70 opacity-60'
                    }`}
                  >
                    <Checkbox
                      id={`brand-${item.id}`}
                      checked={isSelected(item.id, 'brand')}
                      onCheckedChange={() => handleToggleBrand(item)}
                      disabled={!item.isDeliverable}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {item.isDeliverable ? item.email : 'No valid email'}
                        {item.industry && ` • ${item.industry}`}
                      </p>
                    </div>
                    {!item.isDeliverable && (
                      <Badge variant="outline" className="text-[10px] text-gray-400 shrink-0">
                        No email
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default RecipientSelector;
